import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Plus, Trash2, Edit2, Check, AlertCircle, XCircle } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
interface GlossaryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}
export const GlossaryDrawer: React.FC<GlossaryDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const {
    glossary,
    addGlossaryEntry,
    updateGlossaryEntry,
    deleteGlossaryEntry
  } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Add form
  const [newEn, setNewEn] = useState('');
  const [newFr, setNewFr] = useState('');
  const [newAvoid, setNewAvoid] = useState('');
  const [newNotes, setNewNotes] = useState('');
  // Edit form
  const [editEn, setEditEn] = useState('');
  const [editFr, setEditFr] = useState('');
  const [editAvoid, setEditAvoid] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const filteredGlossary = glossary.filter(g => g.english.toLowerCase().includes(searchTerm.toLowerCase()) || g.french.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAdd = () => {
    if (newEn.trim() && newFr.trim()) {
      addGlossaryEntry({
        english: newEn.trim(),
        french: newFr.trim(),
        avoid: newAvoid.trim() || undefined,
        notes: newNotes.trim() || undefined
      });
      setIsAdding(false);
      setNewEn('');
      setNewFr('');
      setNewAvoid('');
      setNewNotes('');
    }
  };
  const startEdit = (entry: (typeof glossary)[0]) => {
    setEditingId(entry.id);
    setEditEn(entry.english);
    setEditFr(entry.french);
    setEditAvoid(entry.avoid || '');
    setEditNotes(entry.notes || '');
  };
  const cancelEdit = () => {
    setEditingId(null);
  };
  const saveEdit = () => {
    if (editingId && editEn.trim() && editFr.trim()) {
      updateGlossaryEntry(editingId, {
        english: editEn.trim(),
        french: editFr.trim(),
        avoid: editAvoid.trim() || undefined,
        notes: editNotes.trim() || undefined
      });
      setEditingId(null);
    }
  };
  return <AnimatePresence data-id="element-78">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 z-40" data-id="element-79" />
        
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200" data-id="element-80">
          
            <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-81">
              <div data-id="element-82">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-83">
                  Project Glossary
                </h2>
                <p className="text-sm text-slate-500" data-id="element-84">
                  Firm-preferred terminology
                </p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors" data-id="element-85">
              
                <X className="w-5 h-5" data-id="element-86" />
              </button>
            </div>

            <div className="p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-87">
              <div className="relative" data-id="element-88">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-89" />
                <input type="text" placeholder="Search terms..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" data-id="element-90" />
              
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-id="element-91">
              {filteredGlossary.length === 0 ? <div className="text-center py-12" data-id="element-92">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-93">
                    <AlertCircle className="w-6 h-6 text-slate-400" data-id="element-94" />
                  </div>
                  <h3 className="text-sm font-medium text-slate-900 mb-1" data-id="element-95">
                    No terms found
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-96">
                    Add terms to ensure consistent translations.
                  </p>
                </div> : <div className="space-y-4" data-id="element-97">
                  {filteredGlossary.map(entry => editingId === entry.id /* ── Edit Mode ── */ ? <div key={entry.id} className="p-4 border-2 border-indigo-300 rounded-lg bg-indigo-50/30" data-id="element-98">
                
                        <div className="space-y-2.5" data-id="element-99">
                          <div data-id="element-100">
                            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-101">
                              English
                            </label>
                            <input type="text" value={editEn} onChange={e => setEditEn(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500 bg-white" data-id="element-102" />
                    
                          </div>
                          <div data-id="element-103">
                            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-104">
                              French
                            </label>
                            <input type="text" value={editFr} onChange={e => setEditFr(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500 bg-white" data-id="element-105" />
                    
                          </div>
                          <div data-id="element-106">
                            <label className="block text-[10px] font-semibold text-red-500 uppercase tracking-wider mb-1" data-id="element-107">
                              Avoid (never use)
                            </label>
                            <input type="text" value={editAvoid} onChange={e => setEditAvoid(e.target.value)} placeholder="e.g. prix" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-red-400 bg-white" data-id="element-108" />
                    
                          </div>
                          <div data-id="element-109">
                            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-110">
                              Notes
                            </label>
                            <input type="text" value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Optional context" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500 bg-white" data-id="element-111" />
                    
                          </div>
                          <div className="flex gap-2 pt-1" data-id="element-112">
                            <button onClick={saveEdit} disabled={!editEn.trim() || !editFr.trim()} className="flex-1 bg-indigo-600 text-white py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5" data-id="element-113">
                      
                              <Check className="w-3.5 h-3.5" data-id="element-114" /> Save
                            </button>
                            <button onClick={cancelEdit} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-115">
                      
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div> /* ── View Mode ── */ : <div key={entry.id} className="p-4 border border-slate-200 rounded-lg bg-white group hover:border-indigo-200 transition-colors" data-id="element-116">
                
                        <div className="flex justify-between items-start" data-id="element-117">
                          <div className="flex-1 min-w-0" data-id="element-118">
                            <div className="font-medium text-slate-900" data-id="element-119">
                              {entry.english}
                            </div>
                            <div className="text-indigo-600 font-medium mt-0.5" data-id="element-120">
                              {entry.french}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" data-id="element-121">
                            <button onClick={() => startEdit(entry)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" data-id="element-122">
                      
                              <Edit2 className="w-3.5 h-3.5" data-id="element-123" />
                            </button>
                            <button onClick={() => deleteGlossaryEntry(entry.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-124">
                      
                              <Trash2 className="w-3.5 h-3.5" data-id="element-125" />
                            </button>
                          </div>
                        </div>
                        {entry.avoid && <div className="text-xs text-red-600 bg-red-50 inline-flex items-center gap-1 px-2 py-1 rounded mt-2.5 mr-2" data-id="element-126">
                            <XCircle className="w-3 h-3" data-id="element-127" /> Avoid: {entry.avoid}
                          </div>}
                        {entry.notes && <div className="text-xs text-slate-500 mt-2.5 bg-slate-50 p-2 rounded" data-id="element-128">
                            {entry.notes}
                          </div>}
                      </div>)}
                </div>}
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50" data-id="element-129">
              {!isAdding ? <button onClick={() => {
            setIsAdding(true);
            setEditingId(null);
          }} className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2" data-id="element-130">
              
                  <Plus className="w-4 h-4" data-id="element-131" />
                  Add New Term
                </button> : <div className="space-y-2.5 bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-132">
                  <div data-id="element-133">
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-134">
                      English
                    </label>
                    <input type="text" placeholder="English term" value={newEn} onChange={e => setNewEn(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500" data-id="element-135" />
                
                  </div>
                  <div data-id="element-136">
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-137">
                      French
                    </label>
                    <input type="text" placeholder="French preferred term" value={newFr} onChange={e => setNewFr(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500" data-id="element-138" />
                
                  </div>
                  <div data-id="element-139">
                    <label className="block text-[10px] font-semibold text-red-500 uppercase tracking-wider mb-1" data-id="element-140">
                      Avoid (never use)
                    </label>
                    <input type="text" placeholder="e.g. prix" value={newAvoid} onChange={e => setNewAvoid(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-red-400" data-id="element-141" />
                
                  </div>
                  <div data-id="element-142">
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-143">
                      Notes
                    </label>
                    <input type="text" placeholder="Optional context" value={newNotes} onChange={e => setNewNotes(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-500" data-id="element-144" />
                
                  </div>
                  <div className="flex gap-2 pt-1" data-id="element-145">
                    <button onClick={handleAdd} disabled={!newEn.trim() || !newFr.trim()} className="flex-1 bg-indigo-600 text-white py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-146">
                  
                      <Check className="w-4 h-4" data-id="element-147" /> Save Term
                    </button>
                    <button onClick={() => setIsAdding(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-148">
                  
                      Cancel
                    </button>
                  </div>
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};